/**
 * SurveyBuilderPage — VER-108 → VER-109
 *
 * Native survey constructor hub (route: /survey/builder). Lists the read-only
 * reference scales (DEMO_SURVEYS) plus user-built surveys, which now round-trip
 * through injected async persistence callbacks. Actions: New survey, Edit, Clone
 * (from a reference scale OR another survey — the template library), Preview/Take,
 * Save draft, Publish (version bump), Delete.
 *
 * PERSISTENCE: the three callbacks default to an in-memory mock so the page runs
 * standalone (and under the current route with no props). The backend agent wires
 * them to client/api/survey.ts — see builder/INTEGRATION.md.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    Typography,
} from "@mui/material";
import { AddOutlined, ContentCopyOutlined, EditOutlined, PlayArrowOutlined, QuizOutlined } from "@mui/icons-material";
import { useMobile } from "../../../hooks/useMobile";
import { VPill, VPillPositive } from "../../../core/components/VPill";
import { ConfirmDialog } from "../../../core/components/ConfirmDialog";
import type { SurveyDefinition } from "./survey.types";
import { allQuestions } from "./survey.types";
import { DEMO_SURVEYS } from "./demoSurveys";
import { cloneSurvey, newSurvey } from "./builder.mock";
import { mockPersistence, SurveyPersistence } from "./builder.persistence";
import { SurveyEditor } from "./SurveyEditor";
import { SurveyRunner } from "./SurveyRunner";

export type SurveyBuilderPageProps = Partial<SurveyPersistence>;

type View = { mode: "list" } | { mode: "edit"; survey: SurveyDefinition } | { mode: "run"; survey: SurveyDefinition };

// ---------------------------------------------------------------------------
// Survey summary card
// ---------------------------------------------------------------------------
type SurveyCardProps = {
    survey: SurveyDefinition;
    onEdit?: () => void;
    onClone: () => void;
    onRun: () => void;
    onDelete?: () => void;
};

const SurveyCard = ({ survey, onEdit, onClone, onRun, onDelete }: SurveyCardProps): JSX.Element => (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <CardContent sx={{ flex: 1 }}>
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} mb={1}>
                <Typography variant="h6" component="h2">
                    {survey.title || "Untitled survey"}
                </Typography>
                {survey.isSystemSeed ? (
                    <VPill size="small" label="Reference" color="default" />
                ) : survey.status === "published" ? (
                    <VPillPositive size="small" label={`Published v${survey.version}`} />
                ) : (
                    <VPill size="small" label="Draft" color="default" />
                )}
            </Box>

            <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                {survey.code ? <Chip size="small" variant="outlined" label={survey.code} /> : null}
                <Chip size="small" variant="outlined" label={`${allQuestions(survey).length} questions`} />
                <Chip
                    size="small"
                    variant="outlined"
                    label={`${survey.sections.length} section${survey.sections.length === 1 ? "" : "s"}`}
                />
            </Box>

            <Typography
                variant="body2"
                color="textSecondary"
                sx={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}
            >
                {survey.instructions || "No instructions yet."}
            </Typography>
        </CardContent>

        <Divider />
        <Box display="flex" gap={1} p={2} flexWrap="wrap">
            <Button size="small" startIcon={<PlayArrowOutlined />} onClick={onRun}>
                {survey.isSystemSeed ? "Preview" : "Take"}
            </Button>
            {onEdit ? (
                <Button size="small" startIcon={<EditOutlined />} onClick={onEdit}>
                    Edit
                </Button>
            ) : null}
            <Button size="small" startIcon={<ContentCopyOutlined />} onClick={onClone}>
                {survey.isSystemSeed ? "Clone" : "Duplicate"}
            </Button>
            {onDelete ? (
                <Button size="small" color="error" onClick={onDelete}>
                    Delete
                </Button>
            ) : null}
        </Box>
    </Card>
);

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const SurveyBuilderPage = (props: SurveyBuilderPageProps): JSX.Element => {
    const { isMobile, isTablet } = useMobile();
    const padding = !isTablet && isMobile ? 2 : 4;

    const onLoadSurveys = props.onLoadSurveys ?? mockPersistence.onLoadSurveys;
    const onSaveSurvey = props.onSaveSurvey ?? mockPersistence.onSaveSurvey;
    const onPublishSurvey = props.onPublishSurvey ?? mockPersistence.onPublishSurvey;
    const onDeleteSurvey = props.onDeleteSurvey ?? mockPersistence.onDeleteSurvey;

    const [userSurveys, setUserSurveys] = useState<SurveyDefinition[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [view, setView] = useState<View>({ mode: "list" });
    const [pendingDelete, setPendingDelete] = useState<SurveyDefinition | null>(null);
    // A published survey can't be edited in place — editing it offers a clone instead.
    const [pendingPublishedEdit, setPendingPublishedEdit] = useState<SurveyDefinition | null>(null);

    // Draft → open the editor directly. Published → warn, then clone to edit.
    const handleEdit = (survey: SurveyDefinition) => {
        if (survey.status === "published") {
            setPendingPublishedEdit(survey);
        } else {
            setView({ mode: "edit", survey });
        }
    };

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const surveys = await onLoadSurveys();
            setUserSurveys(surveys);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Failed to load surveys");
        } finally {
            setLoading(false);
        }
    }, [onLoadSurveys]);

    useEffect(() => {
        void reload();
    }, [reload]);

    const handleSaveDraft = async (draft: SurveyDefinition) => {
        await onSaveSurvey({ ...draft, status: "draft" });
        await reload();
        setView({ mode: "list" });
    };

    const handlePublish = async (draft: SurveyDefinition) => {
        // Persist the latest edits first, then flip to published (bumps version). Publish the id
        // the save RETURNS, not draft.id — a first save creates the row and mints a new server id,
        // so the original client-side id would no longer resolve.
        const saved = await onSaveSurvey({ ...draft, status: "draft" });
        await onPublishSurvey(saved.id);
        await reload();
        setView({ mode: "list" });
    };

    const handleCloneToEditor = (source: SurveyDefinition) => {
        // Clone is not persisted until the user saves it from the editor.
        setView({ mode: "edit", survey: cloneSurvey(source) });
    };

    const handleConfirmDelete = async () => {
        if (!pendingDelete) {
            return;
        }
        if (onDeleteSurvey) {
            await onDeleteSurvey(pendingDelete.id);
        }
        setPendingDelete(null);
        await reload();
    };

    // ---- Editor view ----------------------------------------------------------
    if (view.mode === "edit") {
        return (
            <Box p={padding} width={1}>
                {!isTablet && isMobile ? <Box pt={8} /> : null}
                <Typography variant="h2" color="textPrimary" mb={3}>
                    {view.survey.isSystemSeed ? "Reference scale" : "Survey editor"}
                </Typography>
                <Card>
                    <CardContent>
                        <SurveyEditor
                            survey={view.survey}
                            onSave={handleSaveDraft}
                            onPublish={handlePublish}
                            onCancel={() => setView({ mode: "list" })}
                            onClone={handleCloneToEditor}
                        />
                    </CardContent>
                </Card>
            </Box>
        );
    }

    // ---- Runner / preview view ------------------------------------------------
    if (view.mode === "run") {
        return (
            <Box p={padding} width={1}>
                {!isTablet && isMobile ? <Box pt={8} /> : null}
                <Card>
                    <CardContent>
                        <SurveyRunner
                            survey={view.survey}
                            previewBanner={
                                <Alert severity="info" sx={{ mb: 3 }}>
                                    {view.survey.isSystemSeed
                                        ? "Previewing a read-only reference scale. Responses are not saved."
                                        : "Taking a draft survey. Responses are not saved in this preview."}
                                </Alert>
                            }
                            onExit={() => setView({ mode: "list" })}
                        />
                    </CardContent>
                </Card>
            </Box>
        );
    }

    // ---- List view ------------------------------------------------------------
    return (
        <Box p={padding} width={1}>
            {!isTablet && isMobile ? <Box pt={8} /> : null}

            <Box mb={4} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box display="flex" alignItems="center" gap={1}>
                    <QuizOutlined color="primary" sx={{ fontSize: "2rem" }} />
                    <Typography variant="h2" color="textPrimary">
                        Survey builder
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddOutlined />}
                    onClick={() => setView({ mode: "edit", survey: newSurvey() })}
                >
                    New survey
                </Button>
            </Box>

            {error ? (
                <Alert severity="error" sx={{ mb: 3 }} action={<Button onClick={() => void reload()}>Retry</Button>}>
                    {error}
                </Alert>
            ) : null}

            {/* Reference scales — the template library */}
            <Typography variant="h5" color="textPrimary" mb={2}>
                Reference scales
            </Typography>
            <Grid container spacing={2} mb={4}>
                {DEMO_SURVEYS.map((survey) => (
                    <Grid item xs={12} sm={6} md={4} key={survey.id}>
                        <SurveyCard
                            survey={survey}
                            onClone={() => handleCloneToEditor(survey)}
                            onRun={() => setView({ mode: "run", survey })}
                        />
                    </Grid>
                ))}
            </Grid>

            {/* User surveys */}
            <Typography variant="h5" color="textPrimary" mb={2}>
                Your surveys
            </Typography>
            {loading ? (
                <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress />
                </Box>
            ) : userSurveys.length === 0 ? (
                <Card>
                    <CardContent>
                        <Typography variant="body1" color="textSecondary" align="center">
                            No surveys yet. Start with &quot;New survey&quot; above, or clone a reference scale.
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Grid container spacing={2}>
                    {userSurveys.map((survey) => (
                        <Grid item xs={12} sm={6} md={4} key={survey.id}>
                            <SurveyCard
                                survey={survey}
                                onEdit={() => handleEdit(survey)}
                                onClone={() => handleCloneToEditor(survey)}
                                onRun={() => setView({ mode: "run", survey })}
                                onDelete={() => setPendingDelete(survey)}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            <ConfirmDialog
                open={Boolean(pendingDelete)}
                title="Delete survey?"
                confirmButtonText="Delete"
                onConfirm={handleConfirmDelete}
                onCancel={() => setPendingDelete(null)}
            >
                <Typography variant="body2">
                    Delete &quot;{pendingDelete?.title}&quot;? This cannot be undone.
                </Typography>
            </ConfirmDialog>

            <ConfirmDialog
                open={Boolean(pendingPublishedEdit)}
                title="Edit a published survey?"
                confirmButtonText="Clone to edit"
                onConfirm={() => {
                    if (pendingPublishedEdit) {
                        handleCloneToEditor(pendingPublishedEdit);
                    }
                    setPendingPublishedEdit(null);
                }}
                onCancel={() => setPendingPublishedEdit(null)}
            >
                <Typography variant="body2">
                    &quot;{pendingPublishedEdit?.title}&quot; is published and already in use, so it can&apos;t be
                    edited directly. We&apos;ll create an editable copy (a new draft) — the published version stays
                    unchanged.
                </Typography>
            </ConfirmDialog>
        </Box>
    );
};

export default SurveyBuilderPage;
